import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faBuilding,
  faFileContract,
  faEnvelope,
  faUsers,
  faServer,
  faHeartPulse,
  faGears,
  faClipboardList,
  faGear,
  faUser,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faDatabase,
  faMagnifyingGlass,
  faShield,
  faRightLeft,
  faBoxArchive,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { useUiStore } from '@/stores/ui.store';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';

const GROUPS_KEY = 'platform:sidebar-groups';

interface NavChild {
  label: string;
  to: string;
  icon: typeof faHouse;
  permission?: string;
}

interface NavItem {
  label: string;
  to?: string;
  icon: typeof faHouse;
  permission?: string;
  children?: NavChild[];
}

const PLATFORM_NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    to: PLATFORM_ROUTES.DASHBOARD,
    icon: faHouse,
  },
  {
    label: 'Companies',
    to: PLATFORM_ROUTES.COMPANIES,
    icon: faBuilding,
    permission: 'platform:companies:view',
  },
  {
    label: 'Subscriptions',
    to: PLATFORM_ROUTES.SUBSCRIPTIONS,
    icon: faFileContract,
    permission: 'platform:subscriptions:view',
  },
  {
    label: 'Communication',
    icon: faEnvelope,
    children: [
      {
        label: 'Email Templates',
        to: PLATFORM_ROUTES.COMMUNICATION_TEMPLATES,
        icon: faEnvelope,
        permission: 'platform:templates:view',
      },
      {
        label: 'Email Gateways',
        to: PLATFORM_ROUTES.COMMUNICATION_GATEWAYS,
        icon: faGear,
        permission: 'platform:templates:view',
      },
      {
        label: 'Email Logs',
        to: PLATFORM_ROUTES.COMMUNICATION_LOGS,
        icon: faClipboardList,
        permission: 'platform:templates:view',
      },
    ],
  },
  {
    label: 'Platform Users',
    to: PLATFORM_ROUTES.PLATFORM_USERS,
    icon: faUsers,
    permission: 'platform:platform-users:view',
  },
  {
    label: 'Operations',
    icon: faServer,
    children: [
      {
        label: 'Monitoring',
        to: PLATFORM_ROUTES.OPERATIONS_MONITORING,
        icon: faHeartPulse,
        permission: 'platform:system:view',
      },
      {
        label: 'Background Jobs',
        to: PLATFORM_ROUTES.OPERATIONS_JOBS,
        icon: faGears,
        permission: 'platform:jobs:view',
      },
      {
        label: 'Audit Logs',
        to: PLATFORM_ROUTES.OPERATIONS_AUDIT,
        icon: faClipboardList,
        permission: 'platform:audit-log:view',
      },
    ],
  },
  {
    label: 'Asset Management',
    icon: faDatabase,
    permission: 'platform:system:view',
    children: [
      {
        label: 'Dashboard',
        to: PLATFORM_ROUTES.STORAGE_DASHBOARD,
        icon: faDatabase,
        permission: 'platform:system:view',
      },
      {
        label: 'Asset Explorer',
        to: PLATFORM_ROUTES.STORAGE_ASSETS,
        icon: faMagnifyingGlass,
        permission: 'platform:system:view',
      },
      {
        label: 'Providers',
        to: PLATFORM_ROUTES.STORAGE_PROVIDERS,
        icon: faServer,
        permission: 'platform:system:view',
      },
      {
        label: 'Policies',
        to: PLATFORM_ROUTES.STORAGE_POLICIES,
        icon: faShield,
        permission: 'platform:system:view',
      },
      {
        label: 'Provider Migrations',
        to: PLATFORM_ROUTES.STORAGE_PROVIDER_MIGRATIONS,
        icon: faRightLeft,
        permission: 'platform:system:view',
      },
      {
        label: 'Health',
        to: PLATFORM_ROUTES.STORAGE_HEALTH,
        icon: faHeartPulse,
        permission: 'platform:system:view',
      },
      {
        label: 'Quarantine',
        to: PLATFORM_ROUTES.STORAGE_QUARANTINE,
        icon: faBoxArchive,
        permission: 'platform:system:view',
      },
    ],
  },
  {
    label: 'Settings',
    icon: faGear,
    children: [
      {
        label: 'Platform Settings',
        to: PLATFORM_ROUTES.SETTINGS,
        icon: faGear,
      },
      {
        label: 'Profile',
        to: PLATFORM_ROUTES.PROFILE,
        icon: faUser,
      },
    ],
  },
];

function filterNavItems(
  items: NavItem[],
  can: (perm: string) => boolean,
): NavItem[] {
  return items.reduce<NavItem[]>((acc, item) => {
    if (item.children !== undefined) {
      const visible = item.children.filter(
        (c) => c.permission === undefined || can(c.permission),
      );
      if (visible.length > 0) acc.push({ ...item, children: visible });
    } else {
      if (item.permission === undefined || can(item.permission)) acc.push(item);
    }
    return acc;
  }, []);
}

function getInitialExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(GROUPS_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    // ignore
  }
  return new Set(['Operations', 'Communication', 'Settings', 'Asset Management']);
}

const INACTIVE = 'text-foreground/70';
const INACTIVE_HOVER = 'hover:bg-primary/[0.14] hover:text-foreground';
const ACTIVE_BG = 'bg-primary/[0.22]';
const ACTIVE_TEXT = 'text-primary font-bold';

export function PlatformSidebar() {
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const isMobileSidebarOpen = useUiStore((s) => s.isMobileSidebarOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const { pathname } = useLocation();

  // Phase 12.2A (F-02) — reads the REAL backend field (`role`), which
  // `PlatformUserProfileDto` has exposed since 12.1C. The previous
  // `platformRole` never resolved, so every item below was shown to everyone.
  const platformRole = usePlatformAuthStore((s) => s.user?.role);
  const can = useMemo(
    () => (perm: string) => canPlatform(platformRole, perm),
    [platformRole],
  );

  const visibleItems = useMemo(
    () => filterNavItems(PLATFORM_NAV_ITEMS, can),
    [can],
  );

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(getInitialExpanded);

  const toggleGroup = (label: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify([...next]));
      } catch {
        // ignore
      }
      return next;
    });

  const topLinkCls = ({ isActive }: { isActive: boolean }) => {
    if (sidebarCollapsed) {
      return cn(
        'flex w-full justify-center items-center rounded-lg px-2 py-2 text-sm transition-all duration-150',
        isActive ? cn(ACTIVE_BG, ACTIVE_TEXT) : cn(INACTIVE, INACTIVE_HOVER),
      );
    }
    return cn(
      'flex items-center gap-3 w-full rounded-lg text-sm transition-all duration-150',
      isActive
        ? cn('border-l-[4px] border-primary pl-[8px] pr-3 py-2', ACTIVE_BG, ACTIVE_TEXT)
        : cn('px-3 py-2', INACTIVE, INACTIVE_HOVER),
    );
  };

  const childLinkCls = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-2.5 w-full rounded-lg px-3 py-1.5 text-sm transition-all duration-150',
      isActive ? 'bg-primary/[0.18] text-primary font-semibold' : cn(INACTIVE, INACTIVE_HOVER),
    );

  return (
    <>
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-screen flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300 lg:relative lg:z-auto',
          sidebarCollapsed ? 'w-16' : 'w-64',
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Brand */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-sidebar-border px-3">
          {!sidebarCollapsed && (
            <div className="min-w-0">
              <span className="block truncate text-sm font-bold tracking-tight text-foreground">
                Service Portal
              </span>
              <span className="block text-[10px] font-semibold tracking-widest text-primary uppercase">
                Platform
              </span>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
              INACTIVE,
              INACTIVE_HOVER,
              sidebarCollapsed && 'mx-auto',
            )}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <FontAwesomeIcon
              icon={sidebarCollapsed ? faChevronRight : faChevronLeft}
              className="h-3.5 w-3.5"
            />
          </button>
        </div>

        {/* Navigation */}
        <nav
          className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5"
          aria-label="Platform navigation"
        >
          {visibleItems.map((item) => {
            if (item.children) {
              const isExpanded = expandedGroups.has(item.label);
              const hasActiveChild = item.children.some(
                (c) => pathname === c.to || pathname.startsWith(c.to + '/'),
              );

              return (
                <div key={item.label} className="space-y-0.5">
                  <button
                    onClick={() => !sidebarCollapsed && toggleGroup(item.label)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
                      hasActiveChild ? 'text-primary font-semibold' : cn(INACTIVE, INACTIVE_HOVER),
                      sidebarCollapsed && 'justify-center',
                    )}
                    aria-expanded={isExpanded}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-4 w-4 shrink-0" />
                    {!sidebarCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                          className="h-3 w-3 opacity-50"
                        />
                      </>
                    )}
                  </button>
                  {!sidebarCollapsed && isExpanded && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-sidebar-border pl-2">
                      {item.children.map((child) => (
                        <NavLink key={child.to} to={child.to} className={childLinkCls}>
                          <FontAwesomeIcon icon={child.icon} className="h-3.5 w-3.5 shrink-0" />
                          <span>{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to!}
                end={item.to === PLATFORM_ROUTES.DASHBOARD}
                className={topLinkCls}
                aria-label={sidebarCollapsed ? item.label : undefined}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <FontAwesomeIcon icon={item.icon} className="h-4 w-4 shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
